/**
 * tb-cart-gift-module.js
 * Draft: 2026-07-06 · Shopify Developer (staged, unpublished-theme-copy only)
 * Shared behavior for the cart-page "Is this a gift?" module — identical across
 * Total / Top / Rare. Only the variant IDs baked into each store's snippet differ.
 *
 * Behavior:
 *  - "Add" on the Premium Greeting Card row -> POST /cart/add.js immediately (message
 *    may still be empty; checkout/Pay Now stays blocked until the message is saved).
 *  - Field input (debounced 500ms) -> POST /cart/change.js with line-item properties
 *    {Greeting Card Message, Message} merged onto the existing greeting-card line.
 *    (2026-07-22 RD: To/From inputs REMOVED fleet-wide — message only.)
 *  - "Remove" -> POST /cart/change.js quantity 0 for that line, hide fields, reset button to "Add".
 *  - Gift Wrap "Add" button (independent of the card) -> add/remove the wrap variant via
 *    /cart/add.js and /cart/change.js, swapping the button between "Add" and "Remove".
 *  - After every mutation, dispatch the theme's standard cart-refresh events so subtotal / cart icon
 *    count / cart-drawer stay in sync (mirrors the pattern already used by
 *    assets/auto-checkout-protection.js — see v2.4.3 fix in
 *    07_REPORTS/Dev/drafts/2026-06-29_protection-toggle-cart-page-refresh/).
 *  - No trademarked/AI product imagery — thumbnails are the real hosted assets provided by RD.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function refreshCartUI() {
    // Mirror the fleet-standard refresh contract (v2.4.3): update cart-drawer AND
    // the non-drawer cart-page <cart-items> element so subtotal/qty stay live
    // without a manual page reload.
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    var cartItemsEl = document.querySelector('cart-items#cart-items');
    if (cartItemsEl && typeof cartItemsEl.refresh === 'function') {
      cartItemsEl.refresh();
    }
    // Section rendering API refresh for the cart summary section (subtotal), if theme
    // exposes it globally (Enterprise theme convention — verify at apply time).
    if (window.theme && typeof window.theme.refreshCartSections === 'function') {
      window.theme.refreshCartSections();
    }
  }

  function findLineByVariant(cart, variantId) {
    if (!variantId) return null;
    variantId = String(variantId);
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === variantId) {
        return { line: i + 1, item: cart.items[i] };
      }
    }
    return null;
  }

  // BG-BLANK-CARD 2026-10-21: blank (no-message) Premium Greeting Card opt-in switch.
  var BLANK_KEY = 'Greeting Card';
  var BLANK_PROPS = { 'Greeting Card': 'Premium Greeting Card — Blank (no message)', 'Greeting Card Message': 'None — blank card, leave unwritten' };
  function isBlankCard(item) {
    var p = (item && item.properties) || {};
    return String(p[BLANK_KEY] || '').indexOf('Blank') !== -1;
  }
  function findCardLine(cart, variantId, blank) {
    if (!variantId) return null;
    variantId = String(variantId);
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (String(it.variant_id) === variantId && isBlankCard(it) === !!blank) return { line: i + 1, item: it };
    }
    return null;
  }

  // BG-BLANK-CARD v2: blank card is its own variant (Blank, $7.89); legacy blank lines on the written variant still count as blank.
  function findBlankLine(cart, ids) {
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i], v = String(it.variant_id);
      if (v === String(ids[0]) || (v === String(ids[1]) && isBlankCard(it))) return { line: i + 1, item: it };
    }
    return null;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function cartAdd(variantId, properties) {
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (properties) body.items[0].properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('add-failed');
      return r.json();
    });
  }

  function cartChangeLine(line, quantity, properties) {
    var body = { line: line, quantity: quantity };
    if (properties) body.properties = properties;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('change-failed');
      return r.json();
    });
  }

  class TbCartGiftModule extends HTMLElement {
    connectedCallback() {
      this.cardVariantId = this.dataset.cardVariantId;
      this.wrapVariantId = this.dataset.wrapVariantId;
      this.blankVariantId = this.dataset.blankVariantId || this.cardVariantId;

      this.cardToggleBtn = this.querySelector('[data-role="card-toggle"]');
      this.cardFields = this.querySelector('[data-role="card-fields"]');
      this.messageInput = this.querySelector('[data-role="field-message"]');
      this.charCount = this.querySelector('[data-role="char-count"]');
      this.wrapToggle = this.querySelector('[data-role="wrap-toggle"]');
      this.errorEl = this.querySelector('[data-role="error"]');
      this.blankToggle = this.querySelector('[data-role="blank-toggle"]');

      this._bind();
      this._syncInitialState();
    }

    _bind() {
      this.cardToggleBtn.addEventListener('click', this._onCardToggle.bind(this));
      this.wrapToggle.addEventListener('click', this._onWrapToggle.bind(this));
      if (this.blankToggle) this.blankToggle.addEventListener('click', this._onBlankToggle.bind(this));

      var debouncedSave = debounce(this._saveCardProperties.bind(this), 500);
      this.messageInput.addEventListener('input', debouncedSave);
      this.messageInput.addEventListener('input', this._updateCharCount.bind(this));
      this.messageInput.addEventListener('input', this._markRequired.bind(this));
    }

    _cardMessageFrom(properties) {
      properties = properties || {};
      return properties['Greeting Card Message'] || properties.Message || '';
    }

    _markRequired() {
      var added = this.cardToggleBtn.getAttribute('aria-pressed') === 'true';
      var empty = added && !String(this.messageInput.value || '').trim();
      this.messageInput.classList.toggle('tb-gift-module__input--required-empty', empty);
      this.messageInput.setAttribute('aria-invalid', empty ? 'true' : 'false');
      this.messageInput.required = added;
      if (empty) this.messageInput.setCustomValidity('Add the card message before continuing.');
      else this.messageInput.setCustomValidity('');
    }

    _updateCharCount() {
      var remaining = 250 - this.messageInput.value.length;
      this.charCount.textContent = remaining + ' characters left';
    }

    _showError(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    _clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    // On load, if the greeting-card variant is already in the cart (e.g. page reload
    // after adding), reflect that state: fields visible + populated, button = Remove.
    _syncInitialState() {
      var self = this;
      getCart().then(function (cart) {
        var cardLine = findCardLine(cart, self.cardVariantId, false);
        if (cardLine) {
          self._setCardAddedState(cardLine.item.properties || {});
        }
        self._setBlankVisual(!!findBlankLine(cart, [self.blankVariantId, self.cardVariantId]));
        var wrapLine = findLineByVariant(cart, self.wrapVariantId);
        if (wrapLine) {
          self._setWrapAddedState();
        }
        self._markRequired();
      }).catch(function () { /* non-fatal — leave default UI state */ });
    }

    _setCardAddedState(properties) {
      this.cardToggleBtn.textContent = 'Remove';
      this.cardToggleBtn.setAttribute('aria-pressed', 'true');
      this.cardFields.hidden = false;
      this.messageInput.value = this._cardMessageFrom(properties);
      this._updateCharCount();
      this._markRequired();
    }

    _setCardRemovedState() {
      this.cardToggleBtn.textContent = 'Add';
      this.cardToggleBtn.setAttribute('aria-pressed', 'false');
      this.cardFields.hidden = true;
      this.messageInput.value = '';
      this._updateCharCount();
      this._markRequired();
    }

    _onCardToggle() {
      this._clearError();
      var self = this;
      var adding = this.cardToggleBtn.getAttribute('aria-pressed') !== 'true';

      if (!this.cardVariantId || this.cardVariantId.indexOf('TODO_') === 0) {
        this._showError('Greeting card is not configured for this store yet.');
        return;
      }

      this.cardToggleBtn.disabled = true;

      if (adding) {
        cartAdd(this.cardVariantId)
          .then(function () {
            self._setCardAddedState({});
            self._markRequired();
            refreshCartUI();
          })
          .catch(function () {
            self._showError('Could not add the greeting card. Please try again.');
          })
          .finally(function () { self.cardToggleBtn.disabled = false; });
      } else {
        getCart().then(function (cart) {
          var line = findCardLine(cart, self.cardVariantId, false);
          if (!line) { self._setCardRemovedState(); return Promise.resolve(); }
          return cartChangeLine(line.line, 0).then(function () {
            self._setCardRemovedState();
            refreshCartUI();
          });
        })
          .catch(function () {
            self._showError('Could not remove the greeting card. Please try again.');
          })
          .finally(function () { self.cardToggleBtn.disabled = false; });
      }
    }

    _saveCardProperties() {
      var self = this;
      if (this.cardToggleBtn.getAttribute('aria-pressed') !== 'true') return; // card not in cart yet
      this._markRequired();
      var value = this.messageInput.value.trim().slice(0, 250);
      if (!value) return;

      getCart().then(function (cart) {
        var line = findCardLine(cart, self.cardVariantId, false);
        if (!line) return;
        var properties = Object.assign({}, line.item.properties || {});
        properties['Greeting Card Message'] = value;
        properties.Message = value;
        return cartChangeLine(line.line, line.item.quantity, properties);
      }).then(function () {
        refreshCartUI();
      }).catch(function () {
        self._showError('Could not save your message. Please try again.');
      });
    }

    _setBlankVisual(on) {
      if (!this.blankToggle) return;
      this.blankToggle.textContent = on ? 'Remove' : 'Add';
      this.blankToggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.blankToggle.setAttribute('aria-label', (on ? 'Remove' : 'Add') + ' Blank Premium Greeting Card, $7.89');
    }

    // Blank card (Add button): no text field, no message validation. Own 'Blank' variant at $7.89.
    _onBlankToggle() {
      this._clearError();
      var self = this;
      var goingOn = this.blankToggle.getAttribute('aria-pressed') !== 'true';
      if (!this.blankVariantId) return;
      this.blankToggle.disabled = true;
      this._setBlankVisual(goingOn);
      var work = goingOn
        ? getCart().then(function (cart) {
            if (findBlankLine(cart, [self.blankVariantId, self.cardVariantId])) return null;
            return cartAdd(self.blankVariantId, Object.assign({}, BLANK_PROPS));
          })
        : getCart().then(function (cart) {
            var line = findBlankLine(cart, [self.blankVariantId, self.cardVariantId]);
            if (!line) return null;
            return cartChangeLine(line.line, 0);
          });
      work.then(function () { refreshCartUI(); })
        .catch(function () {
          self._setBlankVisual(!goingOn);
          self._showError(goingOn ? 'Could not add the blank greeting card. Please try again.' : 'Could not remove the blank greeting card. Please try again.');
        })
        .finally(function () { self.blankToggle.disabled = false; });
    }

    _setWrapAddedState() {
      this.wrapToggle.textContent = 'Remove';
      this.wrapToggle.setAttribute('aria-pressed', 'true');
    }

    _setWrapRemovedState() {
      this.wrapToggle.textContent = 'Add';
      this.wrapToggle.setAttribute('aria-pressed', 'false');
    }

    _onWrapToggle() {
      this._clearError();
      var self = this;
      var adding = this.wrapToggle.getAttribute('aria-pressed') !== 'true';

      if (!this.wrapVariantId || this.wrapVariantId.indexOf('TODO_') === 0) {
        this._showError('Gift wrap is not configured for this store yet.');
        return;
      }

      this.wrapToggle.disabled = true;

      if (adding) {
        cartAdd(this.wrapVariantId)
          .then(function () {
            self._setWrapAddedState();
            refreshCartUI();
          })
          .catch(function () {
            self._showError('Could not add gift wrap. Please try again.');
          })
          .finally(function () { self.wrapToggle.disabled = false; });
      } else {
        getCart().then(function (cart) {
          var line = findLineByVariant(cart, self.wrapVariantId);
          if (!line) { self._setWrapRemovedState(); return Promise.resolve(); }
          return cartChangeLine(line.line, 0).then(function () {
            self._setWrapRemovedState();
            refreshCartUI();
          });
        }).catch(function () {
          self._showError('Could not remove gift wrap. Please try again.');
        }).finally(function () { self.wrapToggle.disabled = false; });
      }
    }
  }

  if (!customElements.get('tb-cart-gift-module')) {
    customElements.define('tb-cart-gift-module', TbCartGiftModule);
  }
})();
